/**
 * Hook: useOrganizationInvites
 */

import { useState, useEffect, useCallback } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import {
    getPendingInvites,
    sendInvite as sendInviteService,
    revokeInvite as revokeInviteService,
    reshareInvite as reshareInviteService,
    regenerateInvite as regenerateInviteService,
    buildInviteLink,
    OrganizationInvite,
} from '../../../services/modules/organizationService';
import { fetchOrganizationLimits } from '../../../services/modules/subscription/subscription.limits';
import { updateSubscriptionQuantity } from '../../../services/modules/subscription/subscription.api';
import { notify } from '../../../utils/toast';

async function copyInviteLink(token: string, successMessage = 'Link copiado!') {
    await navigator.clipboard.writeText(buildInviteLink(token));
    notify.success(successMessage);
}

export const useOrganizationInvites = () => {
    const { currentOrganization, refreshOrganizations } = useOrganization();
    const [invites, setInvites] = useState<OrganizationInvite[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadInvites = useCallback(async () => {
        if (!currentOrganization) return;
        try {
            setIsLoading(true);
            setInvites(await getPendingInvites(currentOrganization.id));
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [currentOrganization]);

    useEffect(() => { loadInvites(); }, [loadInvites]);

    const sendInvite = async (email: string, role: 'admin' | 'member' | 'viewer') => {
        if (!currentOrganization) return;
        try {
            setIsLoading(true);
            const link = await sendInviteService(currentOrganization.id, email, role);
            await loadInvites();
            return link;
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const reshareInvite = async (inviteId: string) => {
        try {
            setIsLoading(true);
            const { token, expiresAt } = await reshareInviteService(inviteId);
            setInvites(prev => prev.map(inv =>
                inv.id === inviteId ? { ...inv, token, expires_at: expiresAt } : inv
            ));
            await copyInviteLink(token, 'Validade renovada. Link copiado!');
        } catch (err: any) {
            setError(err.message);
            notify.error(err.message || 'Erro ao reenviar convite.');
        } finally {
            setIsLoading(false);
        }
    };

    const regenerateInvite = async (inviteId: string) => {
        const confirmed = await notify.confirm('Gerar novo link?', {
            description: 'O link anterior deixará de funcionar.',
            confirmText: 'Gerar novo link',
            cancelText: 'Cancelar',
        });
        if (!confirmed) return;

        try {
            setIsLoading(true);
            const { token, expiresAt } = await regenerateInviteService(inviteId);
            setInvites(prev => prev.map(inv =>
                inv.id === inviteId ? { ...inv, token, expires_at: expiresAt } : inv
            ));
            await copyInviteLink(token, 'Novo link copiado!');
        } catch (err: any) {
            setError(err.message);
            notify.error(err.message || 'Erro ao regenerar convite.');
        } finally {
            setIsLoading(false);
        }
    };

    const revokeInvite = async (inviteId: string) => {
        if (!currentOrganization) return;
        try {
            setIsLoading(true);
            await revokeInviteService(inviteId);
            const remainingInvites = await getPendingInvites(currentOrganization.id);
            setInvites(remainingInvites);

            const hasActiveSubscription =
                currentOrganization.subscriptionStatus === 'active' ||
                currentOrganization.subscriptionStatus === 'past_due';

            if (hasActiveSubscription) {
                try {
                    const limits = await fetchOrganizationLimits(currentOrganization.id);
                    const totalSeats = limits.users.max;
                    const usedSeats = limits.users.current + remainingInvites.length;
                    const newQuantity = totalSeats - 1;
                    if (totalSeats > usedSeats && newQuantity >= usedSeats && newQuantity >= 1) {
                        await updateSubscriptionQuantity(currentOrganization.id, newQuantity);
                        notify.success('Convite cancelado e plano ajustado (-1 licença).');
                        await refreshOrganizations();
                        return;
                    }
                } catch (limitError: any) {
                    console.warn('[revokeInvite] Ajuste de licença ignorado:', limitError.message);
                }
            }
            notify.success('Convite cancelado com sucesso!');
        } catch (err: any) {
            setError(err.message);
            notify.error('Erro ao revogar convite.');
        } finally {
            setIsLoading(false);
        }
    };

    return {
        invites,
        isLoading,
        error,
        sendInvite,
        revokeInvite,
        reshareInvite,
        regenerateInvite,
        copyInviteLink: (token: string) => copyInviteLink(token),
        refreshInvites: loadInvites,
    };
};
